import { Search, X } from "lucide-react";
import type { ComponentProps } from "react";

import { controlClass, controlHeight } from "@/components/ui/field";
import { cn } from "@/utils/utils";

/**
 * The one search box.
 *
 * `type="search"` for the platform's own clear affordance and history, plus an
 * explicit clear button — the native one is invisible in several browsers, and
 * "how do I get all the rows back" should never be a question.
 */
export function SearchInput({
  className,
  containerClassName,
  onClear,
  value,
  fieldSize = "md",
  ...props
}: ComponentProps<"input"> & {
  containerClassName?: string;
  onClear?: () => void;
  fieldSize?: keyof typeof controlHeight;
}) {
  const filled = value !== undefined && value !== "";
  // Clearing is a search — it asks for every row back. So it is held whenever
  // the box is, and `readOnly` on an input does not reach a sibling button.
  const held = Boolean(props.readOnly || props.disabled);
  return (
    <div className={cn("relative min-w-0", containerClassName)}>
      <Search
        aria-hidden
        className="text-fg-subtle pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
      />
      <input
        type="search"
        value={value}
        className={cn(
          controlClass,
          controlHeight[fieldSize],
          "pl-9.5",
          filled && onClear ? "pr-9" : "",
          "[&::-webkit-search-cancel-button]:hidden",
          className,
        )}
        {...props}
      />
      {filled && onClear ? (
        <button
          type="button"
          onClick={onClear}
          disabled={held}
          aria-label="Clear search"
          className="text-fg-subtle hover:bg-surface-inset hover:text-fg absolute top-1/2 right-2 grid size-6 -translate-y-1/2 place-items-center rounded-full transition-colors disabled:pointer-events-none disabled:opacity-50"
        >
          <X className="size-3.5" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}
